/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react';
import { desktop, isDesktop } from '../platform';
import { UpdateService } from '../services/UpdateService';

interface UpdateEngineProps {
    isTauri: boolean;
    showToast: (message: string, type?: 'success' | 'error' | 'info') => void;
    openForceUpdateModal: () => void;
}

export const useUpdateEngine = ({ isTauri, showToast, openForceUpdateModal }: UpdateEngineProps) => {
    const [updateStatus, setUpdateStatus] = useState<'idle' | 'checking' | 'available' | 'downloading' | 'ready' | 'uptodate' | 'error'>('idle');
    const [updateError, setUpdateError] = useState<string | null>(null);
    const [availableUpdate, setAvailableUpdate] = useState<any>(null);
    const [currentVersion, setCurrentVersion] = useState<string>('0.0.0');
    const [lastRemoteCheckTime, setLastRemoteCheckTime] = useState<number>(0);

    // Initial Version Check
    useEffect(() => {
        if (isDesktop()) {
            desktop.getVersion().then(setCurrentVersion);
        }
    }, []);

    // System Metadata Listener
    useEffect(() => {
        if (!isDesktop() || currentVersion === '0.0.0') return;

        return UpdateService.listenToUpdaterMetadata((data) => {
            const broadcastTime = data.lastUpdateCheckBroadcast || 0;
            
            if (broadcastTime > lastRemoteCheckTime) {
                setLastRemoteCheckTime(broadcastTime);
                handleUpdateBroadcast(data.updatePriority || 1, data.minVersion || '0.0.0');
            }
        });
    }, [lastRemoteCheckTime, currentVersion]);

    const compareVersions = (v1: string, v2: string) => {
        const parts1 = v1.split('.').map(Number);
        const parts2 = v2.split('.').map(Number);
        for (let i = 0; i < 3; i++) {
            if (parts1[i] > parts2[i]) return 1;
            if (parts1[i] < parts2[i]) return -1;
        }
        return 0;
    };

    const checkForUpdates = async () => {
        if (!isDesktop()) return null;
        setUpdateStatus('checking');
        setUpdateError(null);
        try {
            const update = await desktop.checkUpdate();
            if (update?.available) {
                setAvailableUpdate(update);
                setUpdateStatus('available');
                return update;
            } else {
                setUpdateStatus('uptodate');
            }
        } catch (e: any) {
            setUpdateStatus('error');
            setUpdateError(e.message || "Failed to check for updates.");
        }
        return null;
    };

    const handleUpdateBroadcast = async (priority: number, minVersion: string) => {
        // 1. Check for updates first to get the REAL latest version from the server
        const update = await checkForUpdates();
        
        // 2. Safety Check: If admin specified a version that doesn't exist yet on the server,
        // we MUST ignore it to avoid bricking the app.
        const latestAvailable = update?.available ? update.version : currentVersion;
        const isAdminInputInvalid = compareVersions(minVersion, latestAvailable) > 0;

        if (isAdminInputInvalid) {
            console.warn(`Update broadcast ignored: Admin requested v${minVersion} but only v${latestAvailable} is available.`);
            return;
        }

        // 3. Logic Check: Is my version lower than the broadcasted minVersion?
        const isOutdated = compareVersions(currentVersion, minVersion) < 0;

        // 4. PRIORITY 3: FORCE UPDATE
        if (priority === 3 && isOutdated) {
            openForceUpdateModal();
            return;
        }

        // 5. Normal Update Logic (Priority 1 or 2)
        if (update?.available) {
            if (priority === 2) {
                alert(`A critical update (v${update.version}) is available. Please install it to ensure full compatibility.`);
            } else if (priority === 1) {
                showToast(`New update v${update.version} is available!`, 'info');
            }
        }
    };

    const installUpdate = async () => {
        if (!availableUpdate || !isDesktop()) {
            // If called without availableUpdate (e.g. initial force screen), check first
            const update = await checkForUpdates();
            if (!update) return;
        }
        
        setUpdateStatus('downloading');
        try {
            await availableUpdate.downloadAndInstall();
            setUpdateStatus('ready');
        } catch (e: any) {
            setUpdateStatus('error');
            setUpdateError(e.message || "Failed to install update.");
        }
    };

    const restartApp = async () => {
        if (!isDesktop()) return;
        try {
            await desktop.relaunch();
        } catch (e) {
            console.error("Relaunch failed", e);
        }
    };

    return {
        updateStatus,
        updateError,
        availableUpdate,
        currentVersion,
        checkForUpdates,
        installUpdate,
        restartApp
    };
};
